import type { ChangeEvent } from "react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";
import type {
  FormEvent,
  MouseEvent,
} from "react";
import { api, ApiClientError } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import type {
  ProfessionalCredential,
  ProfessionalDocument,
  ProfessionalDocumentSection,
  ProfessionalDocumentType,
  ProfessionalFiscalProfile,
  ProfessionalInsurance,
  ProfessionalMaster,
  ProfessionalPaymentAccount,
  ProfessionalSensitiveProfile,
  ProfessionalVerificationStatus,
} from "../types";

type IntegralTab =
  | "personal"
  | "credentials"
  | "insurances"
  | "fiscal"
  | "payments"
  | "documents";

type DocumentForm = {
  section: ProfessionalDocumentSection;
  document_type: ProfessionalDocumentType;
  credential_id: string;
  insurance_id: string;
  description: string;
  issued_on: string;
  expires_on: string;
  file: File | null;
};

type PersonalForm = {
  document_type: string;
  document_number: string;
  birth_date: string;
  nationality: string;
  personal_email: string;
  personal_phone: string;
  address_line: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone: string;
  relationship_type: string;
  hire_date: string;
  termination_date: string;
  internal_notes: string;
};

type FiscalForm = {
  cuit: string;
  legal_name: string;
  tax_condition: string;
  gross_income_number: string;
  billing_email: string;
  billing_address: string;
  contract_type: string;
  payment_terms_notes: string;
};

type CredentialForm = {
  credential_type: string;
  title: string;
  institution: string;
  registration_number: string;
  jurisdiction: string;
  issued_on: string;
  expires_on: string;
  status: ProfessionalVerificationStatus;
  storage_key: string;
  notes: string;
};

type InsuranceForm = {
  insurer_name: string;
  policy_number: string;
  coverage_description: string;
  starts_on: string;
  expires_on: string;
  status: ProfessionalVerificationStatus;
  storage_key: string;
  notes: string;
};

type PaymentForm = {
  account_type: string;
  bank_name: string;
  holder_name: string;
  holder_tax_id: string;
  cbu_cvu: string;
  account_alias: string;
  currency: string;
  is_primary: boolean;
};

const EMPTY_DOCUMENT: DocumentForm = {
  section: "PERSONAL",
  document_type: "DNI",
  credential_id: "",
  insurance_id: "",
  description: "",
  issued_on: "",
  expires_on: "",
  file: null,
};

const EMPTY_PERSONAL: PersonalForm = {
  document_type: "",
  document_number: "",
  birth_date: "",
  nationality: "",
  personal_email: "",
  personal_phone: "",
  address_line: "",
  city: "",
  province: "",
  postal_code: "",
  country: "Argentina",
  emergency_contact_name: "",
  emergency_contact_relationship: "",
  emergency_contact_phone: "",
  relationship_type: "",
  hire_date: "",
  termination_date: "",
  internal_notes: "",
};

const EMPTY_FISCAL: FiscalForm = {
  cuit: "",
  legal_name: "",
  tax_condition: "",
  gross_income_number: "",
  billing_email: "",
  billing_address: "",
  contract_type: "",
  payment_terms_notes: "",
};

const EMPTY_CREDENTIAL: CredentialForm = {
  credential_type: "",
  title: "",
  institution: "",
  registration_number: "",
  jurisdiction: "",
  issued_on: "",
  expires_on: "",
  status: "PENDING",
  storage_key: "",
  notes: "",
};

const EMPTY_INSURANCE: InsuranceForm = {
  insurer_name: "",
  policy_number: "",
  coverage_description: "",
  starts_on: "",
  expires_on: "",
  status: "PENDING",
  storage_key: "",
  notes: "",
};

const EMPTY_PAYMENT: PaymentForm = {
  account_type: "BANK",
  bank_name: "",
  holder_name: "",
  holder_tax_id: "",
  cbu_cvu: "",
  account_alias: "",
  currency: "ARS",
  is_primary: false,
};

function text(
  value: string | null | undefined
) {
  return value || "";
}

function nullable(value: string) {
  const trimmed = value.trim();
  return trimmed || null;
}

function errorMessage(
  error: unknown,
  fallback: string
) {
  return error instanceof ApiClientError
    ? error.message
    : fallback;
}

function personalFormFrom(
  profile:
    | ProfessionalSensitiveProfile
    | null
): PersonalForm {
  if (!profile) {
    return { ...EMPTY_PERSONAL };
  }

  return {
    document_type:
      text(profile.document_type),
    document_number:
      text(profile.document_number),
    birth_date:
      text(profile.birth_date),
    nationality:
      text(profile.nationality),
    personal_email:
      text(profile.personal_email),
    personal_phone:
      text(profile.personal_phone),
    address_line:
      text(profile.address_line),
    city: text(profile.city),
    province: text(profile.province),
    postal_code:
      text(profile.postal_code),
    country:
      text(profile.country) ||
      "Argentina",
    emergency_contact_name:
      text(
        profile.emergency_contact_name
      ),
    emergency_contact_relationship:
      text(
        profile
          .emergency_contact_relationship
      ),
    emergency_contact_phone:
      text(
        profile.emergency_contact_phone
      ),
    relationship_type:
      text(profile.relationship_type),
    hire_date:
      text(profile.hire_date),
    termination_date:
      text(profile.termination_date),
    internal_notes:
      text(profile.internal_notes),
  };
}

function fiscalFormFrom(
  profile:
    | ProfessionalFiscalProfile
    | null
): FiscalForm {
  if (!profile) {
    return { ...EMPTY_FISCAL };
  }

  return {
    cuit: text(profile.cuit),
    legal_name:
      text(profile.legal_name),
    tax_condition:
      text(profile.tax_condition),
    gross_income_number:
      text(
        profile.gross_income_number
      ),
    billing_email:
      text(profile.billing_email),
    billing_address:
      text(profile.billing_address),
    contract_type:
      text(profile.contract_type),
    payment_terms_notes:
      text(
        profile.payment_terms_notes
      ),
  };
}

export default function ProfessionalIntegralProfile({
  professional,
  onClose,
}: {
  professional: ProfessionalMaster;
  onClose: () => void;
}) {
  const { hasPermission } = useAuth();
  const { show } = useToast();

  const canManage = hasPermission(
    "professionals.sensitive.manage"
  );

  const [activeTab, setActiveTab] =
    useState<IntegralTab>("personal");

  const [loading, setLoading] =
    useState(true);

  const [personal, setPersonal] =
    useState<PersonalForm>({
      ...EMPTY_PERSONAL,
    });

  const [fiscal, setFiscal] =
    useState<FiscalForm>({
      ...EMPTY_FISCAL,
    });

  const [credentials, setCredentials] =
    useState<ProfessionalCredential[]>(
      []
    );

  const [insurances, setInsurances] =
    useState<ProfessionalInsurance[]>(
      []
    );

  const [payments, setPayments] =
    useState<
      ProfessionalPaymentAccount[]
    >([]);

  const [documents, setDocuments] =
    useState<ProfessionalDocument[]>([]);

  const [documentForm, setDocumentForm] =
    useState<DocumentForm>({
      ...EMPTY_DOCUMENT,
    });

  const [uploadingDocument, setUploadingDocument] =
    useState(false);

  const [downloadingDocumentId, setDownloadingDocumentId] =
    useState<string>("");

  const [deletingDocumentId, setDeletingDocumentId] =
    useState<string>("");

  const [editingDocumentId, setEditingDocumentId] =
    useState<string>("");

  const [editDocumentForm, setEditDocumentForm] =
    useState<DocumentForm>({
      ...EMPTY_DOCUMENT,
    });

  const [savingDocumentMetadata, setSavingDocumentMetadata] =
    useState(false);

  const [savingPersonal, setSavingPersonal] =
    useState(false);

  const [savingFiscal, setSavingFiscal] =
    useState(false);

  const [credentialForm, setCredentialForm] =
    useState<CredentialForm>({
      ...EMPTY_CREDENTIAL,
    });

  const [insuranceForm, setInsuranceForm] =
    useState<InsuranceForm>({
      ...EMPTY_INSURANCE,
    });

  const [paymentForm, setPaymentForm] =
    useState<PaymentForm>({
      ...EMPTY_PAYMENT,
    });

  const [savingCredential, setSavingCredential] =
    useState(false);

  const [savingInsurance, setSavingInsurance] =
    useState(false);

  const [savingPayment, setSavingPayment] =
    useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [
        personalRow,
        fiscalRow,
        credentialRows,
        insuranceRows,
        paymentRows,
        documentRows,
      ] = await Promise.all([
        api.getProfessionalSensitiveProfile(
          professional.id
        ),
        api.getProfessionalFiscalProfile(
          professional.id
        ),
        api.getProfessionalCredentials(
          professional.id
        ),
        api.getProfessionalInsurances(
          professional.id
        ),
        api.getProfessionalPaymentAccounts(
          professional.id
        ),
        api.professionalDocuments(
          professional.id
        ),
      ]);

      setPersonal(
        personalFormFrom(personalRow)
      );

      setFiscal(
        fiscalFormFrom(fiscalRow)
      );

      setCredentials(credentialRows);
      setInsurances(insuranceRows);
      setPayments(paymentRows);
      setDocuments(documentRows);
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo cargar la ficha integral."
        ),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [professional.id, show]);

  useEffect(() => {
    load();
  }, [load]);

  async function savePersonal(
    event: FormEvent
  ) {
    event.preventDefault();

    if (
      personal.hire_date &&
      personal.termination_date &&
      personal.termination_date <
        personal.hire_date
    ) {
      show(
        "La fecha de finalización no puede ser anterior al ingreso.",
        "error"
      );
      return;
    }

    setSavingPersonal(true);

    try {
      const saved =
        await api.updateProfessionalSensitiveProfile(
          professional.id,
          {
            document_type:
              nullable(
                personal.document_type
              ),
            document_number:
              nullable(
                personal.document_number
              ),
            birth_date:
              nullable(
                personal.birth_date
              ),
            nationality:
              nullable(
                personal.nationality
              ),
            personal_email:
              nullable(
                personal.personal_email
              ),
            personal_phone:
              nullable(
                personal.personal_phone
              ),
            address_line:
              nullable(
                personal.address_line
              ),
            city: nullable(personal.city),
            province:
              nullable(personal.province),
            postal_code:
              nullable(
                personal.postal_code
              ),
            country:
              nullable(personal.country),
            emergency_contact_name:
              nullable(
                personal
                  .emergency_contact_name
              ),
            emergency_contact_relationship:
              nullable(
                personal
                  .emergency_contact_relationship
              ),
            emergency_contact_phone:
              nullable(
                personal
                  .emergency_contact_phone
              ),
            relationship_type:
              nullable(
                personal.relationship_type
              ),
            hire_date:
              nullable(
                personal.hire_date
              ),
            termination_date:
              nullable(
                personal.termination_date
              ),
            internal_notes:
              nullable(
                personal.internal_notes
              ),
          }
        );

      setPersonal(
        personalFormFrom(saved)
      );

      show(
        "Datos personales y laborales guardados.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudieron guardar los datos personales."
        ),
        "error"
      );
    } finally {
      setSavingPersonal(false);
    }
  }

  async function saveFiscal(
    event: FormEvent
  ) {
    event.preventDefault();
    setSavingFiscal(true);

    try {
      const saved =
        await api.updateProfessionalFiscalProfile(
          professional.id,
          {
            cuit:
              nullable(fiscal.cuit),
            legal_name:
              nullable(
                fiscal.legal_name
              ),
            tax_condition:
              nullable(
                fiscal.tax_condition
              ),
            gross_income_number:
              nullable(
                fiscal
                  .gross_income_number
              ),
            billing_email:
              nullable(
                fiscal.billing_email
              ),
            billing_address:
              nullable(
                fiscal.billing_address
              ),
            contract_type:
              nullable(
                fiscal.contract_type
              ),
            payment_terms_notes:
              nullable(
                fiscal
                  .payment_terms_notes
              ),
          }
        );

      setFiscal(
        fiscalFormFrom(saved)
      );

      show(
        "Información fiscal guardada.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo guardar la información fiscal."
        ),
        "error"
      );
    } finally {
      setSavingFiscal(false);
    }
  }

  async function createCredential(
    event: FormEvent
  ) {
    event.preventDefault();

    if (
      !credentialForm
        .credential_type
        .trim() ||
      !credentialForm.title.trim()
    ) {
      show(
        "Completá el tipo y el título de la credencial.",
        "error"
      );
      return;
    }

    if (
      credentialForm.issued_on &&
      credentialForm.expires_on &&
      credentialForm.expires_on <
        credentialForm.issued_on
    ) {
      show(
        "El vencimiento no puede ser anterior a la emisión.",
        "error"
      );
      return;
    }

    setSavingCredential(true);

    try {
      const created =
        await api.createProfessionalCredential(
          professional.id,
          {
            credential_type:
              credentialForm
                .credential_type
                .trim(),
            title:
              credentialForm
                .title
                .trim(),
            institution:
              nullable(
                credentialForm
                  .institution
              ),
            registration_number:
              nullable(
                credentialForm
                  .registration_number
              ),
            jurisdiction:
              nullable(
                credentialForm
                  .jurisdiction
              ),
            issued_on:
              nullable(
                credentialForm
                  .issued_on
              ),
            expires_on:
              nullable(
                credentialForm
                  .expires_on
              ),
            status:
              credentialForm.status,
            storage_key:
              nullable(
                credentialForm
                  .storage_key
              ),
            notes:
              nullable(
                credentialForm.notes
              ),
          }
        );

      setCredentials((current) => [
        created,
        ...current,
      ]);

      setCredentialForm({
        ...EMPTY_CREDENTIAL,
      });

      show(
        "Credencial agregada.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo agregar la credencial."
        ),
        "error"
      );
    } finally {
      setSavingCredential(false);
    }
  }

  async function createInsurance(
    event: FormEvent
  ) {
    event.preventDefault();

    if (
      !insuranceForm
        .insurer_name
        .trim() ||
      !insuranceForm
        .policy_number
        .trim()
    ) {
      show(
        "Completá aseguradora y número de póliza.",
        "error"
      );
      return;
    }

    if (
      insuranceForm.starts_on &&
      insuranceForm.expires_on &&
      insuranceForm.expires_on <
        insuranceForm.starts_on
    ) {
      show(
        "El vencimiento no puede ser anterior al inicio.",
        "error"
      );
      return;
    }

    setSavingInsurance(true);

    try {
      const created =
        await api.createProfessionalInsurance(
          professional.id,
          {
            insurer_name:
              insuranceForm
                .insurer_name
                .trim(),
            policy_number:
              insuranceForm
                .policy_number
                .trim(),
            coverage_description:
              nullable(
                insuranceForm
                  .coverage_description
              ),
            starts_on:
              nullable(
                insuranceForm.starts_on
              ),
            expires_on:
              nullable(
                insuranceForm.expires_on
              ),
            status:
              insuranceForm.status,
            storage_key:
              nullable(
                insuranceForm.storage_key
              ),
            notes:
              nullable(
                insuranceForm.notes
              ),
          }
        );

      setInsurances((current) => [
        created,
        ...current,
      ]);

      setInsuranceForm({
        ...EMPTY_INSURANCE,
      });

      show(
        "Seguro agregado.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo agregar el seguro."
        ),
        "error"
      );
    } finally {
      setSavingInsurance(false);
    }
  }

  async function createPayment(
    event: FormEvent
  ) {
    event.preventDefault();

    if (
      !paymentForm.holder_name.trim()
    ) {
      show(
        "Ingresá el titular de la cuenta.",
        "error"
      );
      return;
    }

    if (
      !paymentForm.cbu_cvu.trim() &&
      !paymentForm.account_alias.trim()
    ) {
      show(
        "Ingresá CBU/CVU o alias.",
        "error"
      );
      return;
    }

    setSavingPayment(true);

    try {
      const created =
        await api.createProfessionalPaymentAccount(
          professional.id,
          {
            account_type:
              paymentForm.account_type,
            bank_name:
              nullable(
                paymentForm.bank_name
              ),
            holder_name:
              paymentForm
                .holder_name
                .trim(),
            holder_tax_id:
              nullable(
                paymentForm
                  .holder_tax_id
              ),
            cbu_cvu:
              nullable(
                paymentForm.cbu_cvu
              ),
            account_alias:
              nullable(
                paymentForm
                  .account_alias
              ),
            currency:
              paymentForm.currency,
            is_primary:
              paymentForm.is_primary,
          }
        );

      setPayments((current) => {
        const normalized =
          created.is_primary
            ? current.map((account) => ({
                ...account,
                is_primary: false,
              }))
            : current;

        return [
          created,
          ...normalized,
        ];
      });

      setPaymentForm({
        ...EMPTY_PAYMENT,
      });

      show(
        "Cuenta de pago agregada.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo agregar la cuenta de pago."
        ),
        "error"
      );
    } finally {
      setSavingPayment(false);
    }
  }

  async function uploadDocument(
    event: FormEvent
  ) {
    event.preventDefault();

    const file =
      documentForm.file;

    if (!file) {
      show(
        "Seleccioná un archivo.",
        "error"
      );
      return;
    }

    if (
      file.size <= 0 ||
      file.size > 15 * 1024 * 1024
    ) {
      show(
        "El archivo debe pesar entre 1 byte y 15 MB.",
        "error"
      );
      return;
    }

    const extension =
      file.name
        .split(".")
        .pop()
        ?.toLowerCase() || "";

    const extensionMime =
      extension === "pdf"
        ? "application/pdf"
        : extension === "jpg" ||
            extension === "jpeg"
          ? "image/jpeg"
          : extension === "png"
            ? "image/png"
            : extension === "webp"
              ? "image/webp"
              : extension === "heic"
                ? "image/heic"
                : extension === "heif"
                  ? "image/heif"
                  : "";

    const browserMime =
      file.type
        .split(";")[0]
        .trim()
        .toLowerCase();

    const inferredMime =
      !browserMime ||
      browserMime ===
        "application/octet-stream"
        ? extensionMime
        : browserMime;

    const mimeType =
      inferredMime
        .split(";")[0]
        .trim()
        .toLowerCase();

    const allowedMimeTypes =
      new Set([
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/heif",
      ]);

    if (
      !allowedMimeTypes.has(mimeType)
    ) {
      show(
        "Solo podés cargar PDF, JPG, PNG, WEBP, HEIC o HEIF.",
        "error"
      );
      return;
    }

    if (
      documentForm.expires_on &&
      documentForm.issued_on &&
      documentForm.expires_on <
        documentForm.issued_on
    ) {
      show(
        "El vencimiento no puede ser anterior a la emisión.",
        "error"
      );
      return;
    }

    setUploadingDocument(true);

    let initializedDocumentId:
      ProfessionalDocument["id"] |
      null = null;

    try {
      const initialized =
        await api.initializeProfessionalDocument(
          professional.id,
          {
            section:
              documentForm.section,
            document_type:
              documentForm.document_type,
            original_filename:
              file.name,
            mime_type:
              mimeType,
            size_bytes:
              file.size,
            credential_id:
              documentForm.credential_id ||
              null,
            insurance_id:
              documentForm.insurance_id ||
              null,
            description:
              documentForm.description
                .trim() ||
              null,
            issued_on:
              documentForm.issued_on ||
              null,
            expires_on:
              documentForm.expires_on ||
              null,
          }
        );

      initializedDocumentId =
        initialized.document.id;

      const completed =
        await api.uploadProfessionalDocumentContent(
          professional.id,
          initialized.document.id,
          file,
          mimeType
        );

      setDocuments((current) => [
        completed,
        ...current.filter(
          (item) =>
            String(item.id) !==
            String(completed.id)
        ),
      ]);

      setDocumentForm({
        ...EMPTY_DOCUMENT,
      });

      show(
        "Documento cargado de forma segura.",
        "success"
      );
    } catch (error) {
      if (
        initializedDocumentId != null
      ) {
        try {
          await api.deleteProfessionalDocument(
            professional.id,
            initializedDocumentId
          );
        } catch {
          // La próxima recarga conserva el
          // estado pendiente para revisión.
        }
      }

      show(
        errorMessage(
          error,
          "No se pudo cargar el documento."
        ),
        "error"
      );

      try {
        const refreshed =
          await api.professionalDocuments(
            professional.id
          );

        setDocuments(refreshed);
      } catch {
        // Se conserva la lista actual.
      }
    } finally {
      setUploadingDocument(false);
    }
  }

  function startEditingDocument(
    documentRecord:
      ProfessionalDocument
  ) {
    setEditingDocumentId(
      String(documentRecord.id)
    );

    setEditDocumentForm({
      section:
        documentRecord.section,
      document_type:
        documentRecord.document_type,
      credential_id:
        documentRecord.credential_id == null
          ? ""
          : String(
              documentRecord.credential_id
            ),
      insurance_id:
        documentRecord.insurance_id == null
          ? ""
          : String(
              documentRecord.insurance_id
            ),
      description:
        documentRecord.description || "",
      issued_on:
        documentRecord.issued_on
          ?.slice(0, 10) || "",
      expires_on:
        documentRecord.expires_on
          ?.slice(0, 10) || "",
      file: null,
    });
  }

  function cancelEditingDocument() {
    setEditingDocumentId("");
    setEditDocumentForm({
      ...EMPTY_DOCUMENT,
    });
  }

  async function saveDocumentMetadata(
    event: FormEvent
  ) {
    event.preventDefault();

    if (!editingDocumentId) {
      return;
    }

    if (
      editDocumentForm.expires_on &&
      editDocumentForm.issued_on &&
      editDocumentForm.expires_on <
        editDocumentForm.issued_on
    ) {
      show(
        "El vencimiento no puede ser anterior a la emisión.",
        "error"
      );
      return;
    }

    setSavingDocumentMetadata(true);

    try {
      const updated =
        await api.updateProfessionalDocumentMetadata(
          professional.id,
          editingDocumentId,
          {
            section:
              editDocumentForm.section,
            document_type:
              editDocumentForm.document_type,
            credential_id:
              editDocumentForm.section ===
                "CREDENTIAL"
                ? editDocumentForm
                    .credential_id ||
                  null
                : null,
            insurance_id:
              editDocumentForm.section ===
                "INSURANCE"
                ? editDocumentForm
                    .insurance_id ||
                  null
                : null,
            description:
              editDocumentForm.description
                .trim() ||
              null,
            issued_on:
              editDocumentForm.issued_on ||
              null,
            expires_on:
              editDocumentForm.expires_on ||
              null,
          }
        );

      setDocuments((current) =>
        current.map((item) =>
          String(item.id) ===
          String(updated.id)
            ? updated
            : item
        )
      );

      cancelEditingDocument();

      show(
        "Datos del documento actualizados.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo actualizar el documento."
        ),
        "error"
      );
    } finally {
      setSavingDocumentMetadata(false);
    }
  }

  async function downloadDocument(
    documentRecord:
      ProfessionalDocument
  ) {
    setDownloadingDocumentId(
      String(documentRecord.id)
    );

    try {
      const blob =
        await api.downloadProfessionalDocument(
          professional.id,
          documentRecord.id
        );

      const url =
        URL.createObjectURL(blob);

      const anchor =
        window.document.createElement(
          "a"
        );

      anchor.href = url;
      anchor.download =
        documentRecord.original_filename;
      anchor.style.display = "none";

      window.document.body.appendChild(
        anchor
      );

      anchor.click();
      anchor.remove();

      window.setTimeout(
        () =>
          URL.revokeObjectURL(url),
        1000
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo descargar el documento."
        ),
        "error"
      );
    } finally {
      setDownloadingDocumentId("");
    }
  }

  async function deleteDocument(
    documentRecord:
      ProfessionalDocument
  ) {
    const confirmed =
      window.confirm(
        `¿Eliminar "${documentRecord.original_filename}" del legajo?`
      );

    if (!confirmed) {
      return;
    }

    setDeletingDocumentId(
      String(documentRecord.id)
    );

    try {
      await api.deleteProfessionalDocument(
        professional.id,
        documentRecord.id
      );

      setDocuments((current) =>
        current.filter(
          (item) =>
            String(item.id) !==
            String(documentRecord.id)
        )
      );

      show(
        "Documento eliminado del legajo.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo eliminar el documento."
        ),
        "error"
      );
    } finally {
      setDeletingDocumentId("");
    }
  }

  function setPersonalField(
    field: keyof PersonalForm,
    value: string
  ) {
    setPersonal((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function setFiscalField(
    field: keyof FiscalForm,
    value: string
  ) {
    setFiscal((current) => ({
      ...current,
      [field]: value,
    }));
  }

  return (
    <div
      style={{
        marginTop: 24,
        padding: 18,
        border:
          "1px solid #dce5e8",
        borderRadius: 14,
        background: "#ffffff",
      }}
    >
      <div className="kanban-header">
        <div>
          <h3>
            Ficha integral ·{" "}
            {professional.full_name}
          </h3>

          <p className="meta">
            Información personal, laboral,
            profesional, fiscal y de pago
            protegida por permisos específicos.
          </p>
        </div>

        <button
          type="button"
          className="btn-ghost"
          onClick={onClose}
        >
          Cerrar
        </button>
      </div>

      {!canManage && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            borderRadius: 10,
            background: "#f4f7f8",
            border:
              "1px solid #dce5e8",
          }}
        >
          <span className="meta">
            Tenés acceso de consulta. La
            modificación requiere el permiso
            de administración de datos
            sensibles.
          </span>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <button
          type="button"
          className={
            activeTab === "personal"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("personal")
          }
        >
          Datos personales
        </button>

        <button
          type="button"
          className={
            activeTab === "credentials"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("credentials")
          }
        >
          Matrículas y títulos
        </button>

        <button
          type="button"
          className={
            activeTab === "insurances"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("insurances")
          }
        >
          Seguros
        </button>

        <button
          type="button"
          className={
            activeTab === "fiscal"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("fiscal")
          }
        >
          Fiscal
        </button>

        <button
          type="button"
          className={
            activeTab === "payments"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("payments")
          }
        >
          Cuentas de pago
        </button>

        <button
          type="button"
          className={
            activeTab === "documents"
              ? "btn-primary"
              : "btn-ghost"
          }
          onClick={() =>
            setActiveTab("documents")
          }
        >
          Documentos
        </button>
      </div>

      {loading && (
        <div className="loading-strip">
          Cargando ficha protegida...
        </div>
      )}

      {!loading &&
        activeTab === "personal" && (
          <form
            className="form-grid"
            onSubmit={savePersonal}
          >
            <label>
              Tipo de documento
            </label>

            <select
              value={
                personal.document_type
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "document_type",
                  event.target.value
                )
              }
            >
              <option value="">
                Sin especificar
              </option>
              <option value="DNI">
                DNI
              </option>
              <option value="PASSPORT">
                Pasaporte
              </option>
              <option value="OTHER">
                Otro
              </option>
            </select>

            <label>
              Número de documento
            </label>

            <input
              value={
                personal.document_number
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "document_number",
                  event.target.value
                )
              }
              placeholder="Documento"
            />

            <label>
              Fecha de nacimiento
            </label>

            <input
              type="date"
              value={personal.birth_date}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "birth_date",
                  event.target.value
                )
              }
            />

            <label>
              Nacionalidad
            </label>

            <input
              value={personal.nationality}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "nationality",
                  event.target.value
                )
              }
              placeholder="Ej: Argentina"
            />

            <label>
              Email personal
            </label>

            <input
              type="email"
              value={
                personal.personal_email
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "personal_email",
                  event.target.value
                )
              }
              placeholder="email@ejemplo.com"
            />

            <label>
              Teléfono personal
            </label>

            <input
              value={
                personal.personal_phone
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "personal_phone",
                  event.target.value
                )
              }
              placeholder="+54..."
            />

            <label>
              Domicilio
            </label>

            <input
              value={personal.address_line}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "address_line",
                  event.target.value
                )
              }
              placeholder="Calle, número, piso"
            />

            <label>
              Ciudad
            </label>

            <input
              value={personal.city}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "city",
                  event.target.value
                )
              }
            />

            <label>
              Provincia
            </label>

            <input
              value={personal.province}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "province",
                  event.target.value
                )
              }
            />

            <label>
              Código postal
            </label>

            <input
              value={personal.postal_code}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "postal_code",
                  event.target.value
                )
              }
            />

            <label>
              País
            </label>

            <input
              value={personal.country}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "country",
                  event.target.value
                )
              }
            />

            <label>
              Contacto de emergencia
            </label>

            <input
              value={
                personal
                  .emergency_contact_name
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "emergency_contact_name",
                  event.target.value
                )
              }
              placeholder="Nombre completo"
            />

            <label>
              Vínculo del contacto
            </label>

            <input
              value={
                personal
                  .emergency_contact_relationship
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "emergency_contact_relationship",
                  event.target.value
                )
              }
              placeholder="Ej: familiar"
            />

            <label>
              Teléfono de emergencia
            </label>

            <input
              value={
                personal
                  .emergency_contact_phone
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "emergency_contact_phone",
                  event.target.value
                )
              }
              placeholder="+54..."
            />

            <label>
              Tipo de vínculo laboral
            </label>

            <select
              value={
                personal.relationship_type
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "relationship_type",
                  event.target.value
                )
              }
            >
              <option value="">
                Sin especificar
              </option>
              <option value="EMPLOYEE">
                Relación de dependencia
              </option>
              <option value="CONTRACTOR">
                Prestador independiente
              </option>
              <option value="PARTNER">
                Socio/a
              </option>
              <option value="OTHER">
                Otro
              </option>
            </select>

            <label>
              Fecha de ingreso
            </label>

            <input
              type="date"
              value={personal.hire_date}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "hire_date",
                  event.target.value
                )
              }
            />

            <label>
              Fecha de finalización
            </label>

            <input
              type="date"
              value={
                personal.termination_date
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "termination_date",
                  event.target.value
                )
              }
            />

            <label>
              Notas internas
            </label>

            <textarea
              value={personal.internal_notes}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setPersonalField(
                  "internal_notes",
                  event.target.value
                )
              }
              rows={4}
              placeholder="Información laboral interna protegida"
            />

            {canManage && (
              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingPersonal}
                >
                  {savingPersonal
                    ? "Guardando..."
                    : "Guardar datos personales"}
                </button>
              </div>
            )}
          </form>
        )}

        {activeTab === "fiscal" && (
          <form
            className="form-grid"
            onSubmit={saveFiscal}
          >
            <label>
              CUIT
            </label>

            <input
              value={fiscal.cuit}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "cuit",
                  event.target.value
                )
              }
              placeholder="XX-XXXXXXXX-X"
            />

            <label>
              Razón social
            </label>

            <input
              value={fiscal.legal_name}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "legal_name",
                  event.target.value
                )
              }
            />

            <label>
              Condición fiscal
            </label>

            <input
              value={fiscal.tax_condition}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "tax_condition",
                  event.target.value
                )
              }
              placeholder="Ej: Responsable inscripto, monotributista"
            />

            <label>
              Ingresos Brutos
            </label>

            <input
              value={
                fiscal.gross_income_number
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "gross_income_number",
                  event.target.value
                )
              }
            />

            <label>
              Correo de facturación
            </label>

            <input
              type="email"
              value={fiscal.billing_email}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "billing_email",
                  event.target.value
                )
              }
            />

            <label>
              Domicilio de facturación
            </label>

            <textarea
              value={
                fiscal.billing_address
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "billing_address",
                  event.target.value
                )
              }
              rows={3}
            />

            <label>
              Tipo de contratación
            </label>

            <input
              value={fiscal.contract_type}
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "contract_type",
                  event.target.value
                )
              }
              placeholder="Ej: Relación de dependencia, prestación"
            />

            <label>
              Condiciones y notas de pago
            </label>

            <textarea
              value={
                fiscal.payment_terms_notes
              }
              disabled={!canManage}
              onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                setFiscalField(
                  "payment_terms_notes",
                  event.target.value
                )
              }
              rows={4}
              placeholder="Información fiscal y administrativa protegida"
            />

            {canManage && (
              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingFiscal}
                >
                  {savingFiscal
                    ? "Guardando..."
                    : "Guardar datos fiscales"}
                </button>
              </div>
            )}
          </form>
        )}

        {activeTab === "credentials" && (
          <div
            style={{
              display: "grid",
              gap: 18,
            }}
          >
            <div>
              <h4>
                Matrículas, títulos y certificaciones
              </h4>

              <div
                style={{
                  display: "grid",
                  gap: 10,
                }}
              >
                {credentials.map(
                  (credential) => (
                    <div
                      key={credential.id}
                      style={{
                        padding: 14,
                        border:
                          "1px solid #dce5e8",
                        borderRadius: 12,
                      }}
                    >
                      <strong>
                        {credential.title}
                      </strong>

                      <div className="meta">
                        {credential.credential_type}
                        {credential.institution
                          ? ` · ${credential.institution}`
                          : ""}
                      </div>

                      {credential.registration_number && (
                        <div className="meta">
                          Matrícula o registro:{" "}
                          {
                            credential.registration_number
                          }
                          {credential.jurisdiction
                            ? ` · ${credential.jurisdiction}`
                            : ""}
                        </div>
                      )}

                      <div className="meta">
                        Estado: {credential.status}
                        {credential.issued_on
                          ? ` · Emisión: ${credential.issued_on}`
                          : ""}
                        {credential.expires_on
                          ? ` · Vencimiento: ${credential.expires_on}`
                          : ""}
                      </div>

                      {credential.notes && (
                        <p
                          className="meta"
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          {credential.notes}
                        </p>
                      )}
                    </div>
                  )
                )}

                {credentials.length === 0 && (
                  <div className="empty-state">
                    No hay matrículas, títulos o
                    certificaciones cargadas.
                  </div>
                )}
              </div>
            </div>

            {canManage && (
              <form
                className="form-grid"
                onSubmit={createCredential}
                style={{
                  paddingTop: 18,
                  borderTop:
                    "1px solid #dce5e8",
                }}
              >
                <h4>
                  Agregar credencial
                </h4>

                <span />

                <label>
                  Tipo de credencial
                </label>

                <input
                  value={
                    credentialForm.credential_type
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        credential_type:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Ej: Matrícula, título, certificación"
                  required
                />

                <label>
                  Título o denominación
                </label>

                <input
                  value={credentialForm.title}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        title:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Ej: Médica especialista"
                  required
                />

                <label>
                  Institución
                </label>

                <input
                  value={
                    credentialForm.institution
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        institution:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Matrícula o registro
                </label>

                <input
                  value={
                    credentialForm
                      .registration_number
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        registration_number:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Jurisdicción
                </label>

                <input
                  value={
                    credentialForm.jurisdiction
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        jurisdiction:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Ej: Nacional, CABA, Provincia"
                />

                <label>
                  Fecha de emisión
                </label>

                <input
                  type="date"
                  value={
                    credentialForm.issued_on
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        issued_on:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Fecha de vencimiento
                </label>

                <input
                  type="date"
                  value={
                    credentialForm.expires_on
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        expires_on:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Estado de verificación
                </label>

                <select
                  value={credentialForm.status}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        status:
                          event.target.value as ProfessionalVerificationStatus,
                      })
                    )
                  }
                >
                  <option value="PENDING">
                    Pendiente
                  </option>

                  <option value="VERIFIED">
                    Verificada
                  </option>

                  <option value="EXPIRED">
                    Vencida
                  </option>

                  <option value="REJECTED">
                    Rechazada
                  </option>
                </select>

                <label>
                  Referencia del documento
                </label>

                <input
                  value={
                    credentialForm.storage_key
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        storage_key:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Identificador o ubicación protegida"
                />

                <label>
                  Notas
                </label>

                <textarea
                  value={credentialForm.notes}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setCredentialForm(
                      (current) => ({
                        ...current,
                        notes:
                          event.target.value,
                      })
                    )
                  }
                  rows={3}
                />

                <div className="modal-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingCredential}
                  >
                    {savingCredential
                      ? "Guardando..."
                      : "Agregar credencial"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeTab === "insurances" && (
          <div
            style={{
              display: "grid",
              gap: 18,
            }}
          >
            <div>
              <h4>
                Seguros profesionales
              </h4>

              <div
                style={{
                  display: "grid",
                  gap: 10,
                }}
              >
                {insurances.map(
                  (insurance) => (
                    <div
                      key={insurance.id}
                      style={{
                        padding: 14,
                        border:
                          "1px solid #dce5e8",
                        borderRadius: 12,
                      }}
                    >
                      <strong>
                        {insurance.insurer_name}
                      </strong>

                      <div className="meta">
                        Póliza:{" "}
                        {insurance.policy_number}
                      </div>

                      {insurance.coverage_description && (
                        <div className="meta">
                          Cobertura:{" "}
                          {
                            insurance.coverage_description
                          }
                        </div>
                      )}

                      <div className="meta">
                        Estado: {insurance.status}
                        {insurance.starts_on
                          ? ` · Inicio: ${insurance.starts_on}`
                          : ""}
                        {insurance.expires_on
                          ? ` · Vencimiento: ${insurance.expires_on}`
                          : ""}
                      </div>

                      {insurance.notes && (
                        <p
                          className="meta"
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          {insurance.notes}
                        </p>
                      )}
                    </div>
                  )
                )}

                {insurances.length === 0 && (
                  <div className="empty-state">
                    No hay seguros profesionales
                    cargados.
                  </div>
                )}
              </div>
            </div>

            {canManage && (
              <form
                className="form-grid"
                onSubmit={createInsurance}
                style={{
                  paddingTop: 18,
                  borderTop:
                    "1px solid #dce5e8",
                }}
              >
                <h4>
                  Agregar seguro
                </h4>

                <span />

                <label>
                  Compañía aseguradora
                </label>

                <input
                  value={
                    insuranceForm.insurer_name
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        insurer_name:
                          event.target.value,
                      })
                    )
                  }
                  required
                />

                <label>
                  Número de póliza
                </label>

                <input
                  value={
                    insuranceForm.policy_number
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        policy_number:
                          event.target.value,
                      })
                    )
                  }
                  required
                />

                <label>
                  Descripción de cobertura
                </label>

                <textarea
                  value={
                    insuranceForm
                      .coverage_description
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        coverage_description:
                          event.target.value,
                      })
                    )
                  }
                  rows={3}
                  placeholder="Ej: Responsabilidad civil y mala praxis"
                />

                <label>
                  Inicio de vigencia
                </label>

                <input
                  type="date"
                  value={
                    insuranceForm.starts_on
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        starts_on:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Vencimiento
                </label>

                <input
                  type="date"
                  value={
                    insuranceForm.expires_on
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        expires_on:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Estado de verificación
                </label>

                <select
                  value={insuranceForm.status}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        status:
                          event.target.value as ProfessionalVerificationStatus,
                      })
                    )
                  }
                >
                  <option value="PENDING">
                    Pendiente
                  </option>

                  <option value="VERIFIED">
                    Verificado
                  </option>

                  <option value="EXPIRED">
                    Vencido
                  </option>

                  <option value="REJECTED">
                    Rechazado
                  </option>
                </select>

                <label>
                  Referencia del documento
                </label>

                <input
                  value={
                    insuranceForm.storage_key
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        storage_key:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Identificador o ubicación protegida"
                />

                <label>
                  Notas
                </label>

                <textarea
                  value={insuranceForm.notes}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setInsuranceForm(
                      (current) => ({
                        ...current,
                        notes:
                          event.target.value,
                      })
                    )
                  }
                  rows={3}
                />

                <div className="modal-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingInsurance}
                  >
                    {savingInsurance
                      ? "Guardando..."
                      : "Agregar seguro"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeTab === "payments" && (
          <div
            style={{
              display: "grid",
              gap: 18,
            }}
          >
            <div>
              <h4>
                Cuentas y destinos de pago
              </h4>

              <div
                style={{
                  display: "grid",
                  gap: 10,
                }}
              >
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    style={{
                      padding: 14,
                      border:
                        "1px solid #dce5e8",
                      borderRadius: 12,
                    }}
                  >
                    <strong>
                      {payment.holder_name}
                      {payment.is_primary
                        ? " · Principal"
                        : ""}
                    </strong>

                    <div className="meta">
                      {payment.account_type}
                      {payment.bank_name
                        ? ` · ${payment.bank_name}`
                        : ""}
                      {payment.currency
                        ? ` · ${payment.currency}`
                        : ""}
                    </div>

                    {payment.holder_tax_id && (
                      <div className="meta">
                        CUIT del titular:{" "}
                        {payment.holder_tax_id}
                      </div>
                    )}

                    {payment.cbu_cvu && (
                      <div className="meta">
                        CBU/CVU:{" "}
                        {payment.cbu_cvu}
                      </div>
                    )}

                    {payment.account_alias && (
                      <div className="meta">
                        Alias:{" "}
                        {payment.account_alias}
                      </div>
                    )}

                    {!payment.active && (
                      <div className="meta">
                        Cuenta inactiva
                      </div>
                    )}
                  </div>
                ))}

                {payments.length === 0 && (
                  <div className="empty-state">
                    No hay cuentas de pago
                    cargadas.
                  </div>
                )}
              </div>
            </div>

            {canManage && (
              <form
                className="form-grid"
                onSubmit={createPayment}
                style={{
                  paddingTop: 18,
                  borderTop:
                    "1px solid #dce5e8",
                }}
              >
                <h4>
                  Agregar cuenta de pago
                </h4>

                <span />

                <label>
                  Tipo de cuenta
                </label>

                <select
                  value={
                    paymentForm.account_type
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        account_type:
                          event.target.value,
                      })
                    )
                  }
                >
                  <option value="BANK">
                    Cuenta bancaria
                  </option>

                  <option value="VIRTUAL">
                    Billetera virtual
                  </option>

                  <option value="OTHER">
                    Otro destino
                  </option>
                </select>

                <label>
                  Banco o entidad
                </label>

                <input
                  value={paymentForm.bank_name}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        bank_name:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Titular
                </label>

                <input
                  value={
                    paymentForm.holder_name
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        holder_name:
                          event.target.value,
                      })
                    )
                  }
                  required
                />

                <label>
                  CUIT del titular
                </label>

                <input
                  value={
                    paymentForm.holder_tax_id
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        holder_tax_id:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="XX-XXXXXXXX-X"
                />

                <label>
                  CBU o CVU
                </label>

                <input
                  value={paymentForm.cbu_cvu}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        cbu_cvu:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Alias
                </label>

                <input
                  value={
                    paymentForm.account_alias
                  }
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        account_alias:
                          event.target.value,
                      })
                    )
                  }
                />

                <label>
                  Moneda
                </label>

                <select
                  value={paymentForm.currency}
                  onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                    setPaymentForm(
                      (current) => ({
                        ...current,
                        currency:
                          event.target.value,
                      })
                    )
                  }
                >
                  <option value="ARS">
                    Pesos argentinos
                  </option>

                  <option value="USD">
                    Dólares estadounidenses
                  </option>
                </select>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    style={{
                      width: "auto",
                      flex: "0 0 auto",
                      margin: 0,
                    }}
                    checked={
                      paymentForm.is_primary
                    }
                    onChange={(event: ChangeEvent<
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                  >) =>
                      setPaymentForm(
                        (current) => ({
                          ...current,
                          is_primary:
                            (event.target as HTMLInputElement).checked,
                        })
                      )
                    }
                  />

                  Cuenta principal
                </label>

                <span />

                <div className="modal-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingPayment}
                  >
                    {savingPayment
                      ? "Guardando..."
                      : "Agregar cuenta"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      {!loading &&
        activeTab === "documents" && (
          <div
            style={{
              display: "grid",
              gap: 16,
            }}
          >
            <div>
              <h4>
                Documentación privada
              </h4>

              <p className="meta">
                Archivos protegidos del legajo.
                Solo pueden consultarlos usuarios
                autorizados.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gap: 10,
              }}
            >
              {documents.map(
                (documentRecord) => (
                  <div
                    key={documentRecord.id}
                    style={{
                      padding: 14,
                      border:
                        "1px solid #dce5e8",
                      borderRadius: 12,
                      display: "flex",
                      gap: 14,
                      alignItems:
                        "flex-start",
                      justifyContent:
                        "space-between",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        flex: "1 1 320px",
                        minWidth: 0,
                      }}
                    >
                      <strong
                        style={{
                          overflowWrap:
                            "anywhere",
                        }}
                      >
                        {
                          documentRecord.original_filename
                        }
                      </strong>

                      <div className="meta">
                        {
                          documentRecord.document_type
                        }
                        {" · "}
                        {
                          documentRecord.section
                        }
                        {" · "}
                        {(
                          documentRecord.size_bytes /
                          1024 /
                          1024
                        ).toFixed(2)}
                        {" MB"}
                      </div>

                      <div className="meta">
                        Estado:{" "}
                        {documentRecord.status ===
                        "AVAILABLE"
                          ? "Disponible"
                          : documentRecord.status ===
                              "UPLOADING"
                            ? "Carga pendiente"
                            : "Fallido"}
                      </div>

                      {documentRecord.description && (
                        <p
                          className="meta"
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          {
                            documentRecord.description
                          }
                        </p>
                      )}

                      {(documentRecord.issued_on ||
                        documentRecord.expires_on) && (
                        <div className="meta">
                          {documentRecord.issued_on
                            ? `Emisión: ${documentRecord.issued_on}`
                            : ""}
                          {documentRecord.issued_on &&
                          documentRecord.expires_on
                            ? " · "
                            : ""}
                          {documentRecord.expires_on
                            ? `Vencimiento: ${documentRecord.expires_on}`
                            : ""}
                        </div>
                      )}

                      {documentRecord.uploaded_by_name && (
                        <div className="meta">
                          Cargado por:{" "}
                          {
                            documentRecord.uploaded_by_name
                          }
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {documentRecord.status ===
                        "AVAILABLE" && (
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={
                            downloadingDocumentId ===
                            String(
                              documentRecord.id
                            )
                          }
                          onClick={() =>
                            downloadDocument(
                              documentRecord
                            )
                          }
                        >
                          {downloadingDocumentId ===
                          String(
                            documentRecord.id
                          )
                            ? "Descargando..."
                            : "Descargar"}
                        </button>
                      )}

                      {canManage &&
                        documentRecord.status ===
                          "AVAILABLE" && (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={
                              savingDocumentMetadata
                            }
                            onClick={() =>
                              startEditingDocument(
                                documentRecord
                              )
                            }
                          >
                            Editar
                          </button>
                        )}

                      {canManage && (
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={
                            deletingDocumentId ===
                            String(
                              documentRecord.id
                            )
                          }
                          onClick={() =>
                            deleteDocument(
                              documentRecord
                            )
                          }
                        >
                          {deletingDocumentId ===
                          String(
                            documentRecord.id
                          )
                            ? "Eliminando..."
                            : "Eliminar"}
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}

              {documents.length === 0 && (
                <div className="empty-state">
                  Todavía no hay documentos
                  cargados.
                </div>
              )}
            </div>
          </div>
        )}

      {!loading &&
        activeTab === "documents" &&
        canManage && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 20,
              borderTop:
                "1px solid #dce5e8",
            }}
          >
            <h4>
              Cargar documento privado
            </h4>

            <p className="meta">
              Formatos admitidos: PDF, JPG,
              PNG, WEBP, HEIC y HEIF. Tamaño
              máximo: 15 MB.
            </p>

            <form
              className="form-grid"
              onSubmit={uploadDocument}
            >
              <label>
                Archivo
              </label>

              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      file:
                        event.target.files?.[0] ||
                        null,
                    })
                  )
                }
              />

              <label>
                Sección
              </label>

              <select
                value={documentForm.section}
                onChange={(
                  event: ChangeEvent<HTMLSelectElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      section:
                        event.target.value as ProfessionalDocumentSection,
                      credential_id: "",
                      insurance_id: "",
                    })
                  )
                }
              >
                <option value="PERSONAL">
                  Datos personales
                </option>
                <option value="CREDENTIAL">
                  Matrículas y títulos
                </option>
                <option value="INSURANCE">
                  Seguros
                </option>
                <option value="FISCAL">
                  Fiscal
                </option>
                <option value="PAYMENT">
                  Cuentas de pago
                </option>
                <option value="LABOR">
                  Laboral
                </option>
                <option value="OTHER">
                  Otros
                </option>
              </select>

              <label>
                Tipo de documento
              </label>

              <select
                value={
                  documentForm.document_type
                }
                onChange={(
                  event: ChangeEvent<HTMLSelectElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      document_type:
                        event.target.value as ProfessionalDocumentType,
                    })
                  )
                }
              >
                <option value="DNI">
                  DNI
                </option>
                <option value="PASSPORT">
                  Pasaporte
                </option>
                <option value="TITLE">
                  Título
                </option>
                <option value="REGISTRATION">
                  Matrícula
                </option>
                <option value="CERTIFICATE">
                  Certificado
                </option>
                <option value="INSURANCE_POLICY">
                  Póliza de seguro
                </option>
                <option value="TAX_CERTIFICATE">
                  Constancia fiscal
                </option>
                <option value="CONTRACT">
                  Contrato
                </option>
                <option value="PROFILE_IMAGE">
                  Foto de perfil
                </option>
                <option value="OTHER">
                  Otro
                </option>
              </select>

              {documentForm.section ===
                "CREDENTIAL" && (
                <>
                  <label>
                    Credencial relacionada
                  </label>

                  <select
                    value={
                      documentForm.credential_id
                    }
                    onChange={(
                      event: ChangeEvent<HTMLSelectElement>
                    ) =>
                      setDocumentForm(
                        (current) => ({
                          ...current,
                          credential_id:
                            event.target.value,
                        })
                      )
                    }
                  >
                    <option value="">
                      Sin vincular
                    </option>

                    {credentials
                      .filter(
                        (credential) =>
                          credential.active
                      )
                      .map((credential) => (
                        <option
                          key={credential.id}
                          value={String(
                            credential.id
                          )}
                        >
                          {credential.title}
                          {credential.registration_number
                            ? ` · ${credential.registration_number}`
                            : ""}
                        </option>
                      ))}
                  </select>
                </>
              )}

              {documentForm.section ===
                "INSURANCE" && (
                <>
                  <label>
                    Seguro relacionado
                  </label>

                  <select
                    value={
                      documentForm.insurance_id
                    }
                    onChange={(
                      event: ChangeEvent<HTMLSelectElement>
                    ) =>
                      setDocumentForm(
                        (current) => ({
                          ...current,
                          insurance_id:
                            event.target.value,
                        })
                      )
                    }
                  >
                    <option value="">
                      Sin vincular
                    </option>

                    {insurances
                      .filter(
                        (insurance) =>
                          insurance.active
                      )
                      .map((insurance) => (
                        <option
                          key={insurance.id}
                          value={String(
                            insurance.id
                          )}
                        >
                          {insurance.insurer_name}
                          {" · "}
                          {insurance.policy_number}
                        </option>
                      ))}
                  </select>
                </>
              )}

              <label>
                Descripción
              </label>

              <input
                value={documentForm.description}
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      description:
                        event.target.value,
                    })
                  )
                }
                placeholder="Ej: DNI frente y dorso"
              />

              <label>
                Fecha de emisión
              </label>

              <input
                type="date"
                value={documentForm.issued_on}
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      issued_on:
                        event.target.value,
                    })
                  )
                }
              />

              <label>
                Fecha de vencimiento
              </label>

              <input
                type="date"
                value={documentForm.expires_on}
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setDocumentForm(
                    (current) => ({
                      ...current,
                      expires_on:
                        event.target.value,
                    })
                  )
                }
              />

              {documentForm.file && (
                <>
                  <label>
                    Archivo seleccionado
                  </label>

                  <span className="meta">
                    {documentForm.file.name}
                    {" · "}
                    {(
                      documentForm.file.size /
                      1024 /
                      1024
                    ).toFixed(2)}
                    {" MB"}
                  </span>
                </>
              )}

              <span />

              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    uploadingDocument ||
                    !documentForm.file
                  }
                >
                  {uploadingDocument
                    ? "Cargando..."
                    : "Cargar documento"}
                </button>
              </div>
            </form>
          </div>
        )}

      {editingDocumentId && (
        <div
          className="modal-backdrop"
          onClick={() => {
            if (
              !savingDocumentMetadata
            ) {
              cancelEditingDocument();
            }
          }}
        >
          <div
            className="modal modal-wide"
            onClick={(
              event: MouseEvent<HTMLDivElement>
            ) =>
              event.stopPropagation()
            }
          >
            <h3>
              Editar documento
            </h3>

            <p className="meta">
              Podés corregir los datos del
              documento. El archivo almacenado
              no será modificado.
            </p>

            <form
              className="form-grid"
              onSubmit={
                saveDocumentMetadata
              }
            >
              <label>
                Sección
              </label>

              <select
                value={
                  editDocumentForm.section
                }
                onChange={(
                  event: ChangeEvent<HTMLSelectElement>
                ) =>
                  setEditDocumentForm(
                    (current) => ({
                      ...current,
                      section:
                        event.target.value as ProfessionalDocumentSection,
                      credential_id: "",
                      insurance_id: "",
                    })
                  )
                }
              >
                <option value="PERSONAL">
                  Datos personales
                </option>
                <option value="CREDENTIAL">
                  Matrículas y títulos
                </option>
                <option value="INSURANCE">
                  Seguros
                </option>
                <option value="FISCAL">
                  Fiscal
                </option>
                <option value="PAYMENT">
                  Cuentas de pago
                </option>
                <option value="LABOR">
                  Laboral
                </option>
                <option value="OTHER">
                  Otros
                </option>
              </select>

              <label>
                Tipo de documento
              </label>

              <select
                value={
                  editDocumentForm.document_type
                }
                onChange={(
                  event: ChangeEvent<HTMLSelectElement>
                ) =>
                  setEditDocumentForm(
                    (current) => ({
                      ...current,
                      document_type:
                        event.target.value as ProfessionalDocumentType,
                    })
                  )
                }
              >
                <option value="DNI">
                  DNI
                </option>
                <option value="PASSPORT">
                  Pasaporte
                </option>
                <option value="TITLE">
                  Título
                </option>
                <option value="REGISTRATION">
                  Matrícula
                </option>
                <option value="CERTIFICATE">
                  Certificado
                </option>
                <option value="INSURANCE_POLICY">
                  Póliza de seguro
                </option>
                <option value="TAX_CERTIFICATE">
                  Constancia fiscal
                </option>
                <option value="CONTRACT">
                  Contrato
                </option>
                <option value="PROFILE_IMAGE">
                  Foto de perfil
                </option>
                <option value="OTHER">
                  Otro
                </option>
              </select>

              {editDocumentForm.section ===
                "CREDENTIAL" && (
                <>
                  <label>
                    Credencial relacionada
                  </label>

                  <select
                    value={
                      editDocumentForm
                        .credential_id
                    }
                    onChange={(
                      event: ChangeEvent<HTMLSelectElement>
                    ) =>
                      setEditDocumentForm(
                        (current) => ({
                          ...current,
                          credential_id:
                            event.target.value,
                        })
                      )
                    }
                  >
                    <option value="">
                      Sin vincular
                    </option>

                    {credentials
                      .filter(
                        (credential) =>
                          credential.active
                      )
                      .map((credential) => (
                        <option
                          key={credential.id}
                          value={String(
                            credential.id
                          )}
                        >
                          {credential.title}
                          {credential.registration_number
                            ? ` · ${credential.registration_number}`
                            : ""}
                        </option>
                      ))}
                  </select>
                </>
              )}

              {editDocumentForm.section ===
                "INSURANCE" && (
                <>
                  <label>
                    Seguro relacionado
                  </label>

                  <select
                    value={
                      editDocumentForm
                        .insurance_id
                    }
                    onChange={(
                      event: ChangeEvent<HTMLSelectElement>
                    ) =>
                      setEditDocumentForm(
                        (current) => ({
                          ...current,
                          insurance_id:
                            event.target.value,
                        })
                      )
                    }
                  >
                    <option value="">
                      Sin vincular
                    </option>

                    {insurances
                      .filter(
                        (insurance) =>
                          insurance.active
                      )
                      .map((insurance) => (
                        <option
                          key={insurance.id}
                          value={String(
                            insurance.id
                          )}
                        >
                          {insurance.insurer_name}
                          {" · "}
                          {insurance.policy_number}
                        </option>
                      ))}
                  </select>
                </>
              )}

              <label>
                Descripción
              </label>

              <input
                value={
                  editDocumentForm.description
                }
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setEditDocumentForm(
                    (current) => ({
                      ...current,
                      description:
                        event.target.value,
                    })
                  )
                }
                placeholder="Descripción del documento"
              />

              <label>
                Fecha de emisión
              </label>

              <input
                type="date"
                value={
                  editDocumentForm.issued_on
                }
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setEditDocumentForm(
                    (current) => ({
                      ...current,
                      issued_on:
                        event.target.value,
                    })
                  )
                }
              />

              <label>
                Fecha de vencimiento
              </label>

              <input
                type="date"
                value={
                  editDocumentForm.expires_on
                }
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setEditDocumentForm(
                    (current) => ({
                      ...current,
                      expires_on:
                        event.target.value,
                    })
                  )
                }
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={
                    savingDocumentMetadata
                  }
                  onClick={
                    cancelEditingDocument
                  }
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    savingDocumentMetadata
                  }
                >
                  {savingDocumentMetadata
                    ? "Guardando..."
                    : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
